/**
 * Laufzeit, Kündigungsfrist und Minderjährigkeit eines Vertrags. Gleich halten mit app.contract_earliest_end und
 * app.is_minor_on (0059): Oberfläche und Datenbank rechnen dieselbe Frist und dieselbe Altersgrenze.
 */
import type { IsoDate, Language } from "./types.js";

function parse(d: string): Date {
  return new Date(`${d.slice(0, 10)}T00:00:00Z`);
}
function iso(d: Date): IsoDate {
  return d.toISOString().slice(0, 10) as IsoDate;
}
/** Datum plus Tage (UTC, ohne Zeitzonensprünge). */
export function addDaysIso(d: string, days: number): IsoDate {
  const x = parse(d);
  x.setUTCDate(x.getUTCDate() + days);
  return iso(x);
}
/** Datum plus Monate wie Postgres (Monatsende bleibt Monatsende: 31.01. + 1 Monat = 28./29.02.). */
export function addMonthsIso(d: string, months: number): IsoDate {
  const x = parse(d);
  const day = x.getUTCDate();
  const target = new Date(Date.UTC(x.getUTCFullYear(), x.getUTCMonth() + months, 1));
  const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(day, last));
  return iso(target);
}

export interface TermFields {
  endsOn: string | null;
  autoRenew: boolean;
  renewalMonths: number | null;
  noticeDays: number | null;
}

/**
 * Frühestes ordentliches Vertragsende, wenn die Kündigung am `received` zugeht. Befristet ohne Verlängerung: erst das
 * vereinbarte Ende (ordentlich davor nicht kündbar, VERTRAEGE-R2-02). Mit automatischer Verlängerung: das erste Laufzeitende,
 * zu dem die Frist noch gewahrt ist. Unbefristet: Zugang plus Frist. Ohne Frist (und ohne festes Ende): null.
 */
export function earliestTermination(t: TermFields, received: string): IsoDate | null {
  if (!received) return null;
  if (t.endsOn && !t.autoRenew) return t.endsOn.slice(0, 10) as IsoDate;
  if (t.noticeDays == null) return null;
  if (!t.endsOn) return addDaysIso(received, t.noticeDays);
  let e = t.endsOn.slice(0, 10) as IsoDate;
  const step = Math.max(t.renewalMonths ?? 12, 1);
  for (let i = 0; i < 1000 && addDaysIso(e, -t.noticeDays) < received; i++) e = addMonthsIso(e, step);
  return e;
}

/** Ende aus Beginn und Laufzeit: Beginn + Monate − 1 Tag (wie die Datenbank beim Abschluss). */
export function endFromTerm(start: string, months: number): IsoDate {
  return addDaysIso(addMonthsIso(start, months), -1);
}

/** Kündigungsfrist als Text: ganze Wochen als Wochen, sonst Tage („4 Wochen“, „30 Tage“, „4 weeks“). */
export function noticePeriodText(days: number | null | undefined, lang: Language): string | null {
  if (days == null || !Number.isFinite(days) || days < 0) return null;
  const weeks = days > 0 && days % 7 === 0 ? days / 7 : null;
  if (lang === "en") return weeks ? `${weeks} ${weeks === 1 ? "week" : "weeks"}` : `${days} ${days === 1 ? "day" : "days"}`;
  return weeks ? `${weeks} ${weeks === 1 ? "Woche" : "Wochen"}` : `${days} ${days === 1 ? "Tag" : "Tagen"}`;
}

// ── Minderjährige (Befund 2) ───────────────────────────────────────────────────────────────────────────────────────

/** Alter in vollen Jahren an einem Tag (null ohne Geburtsdatum). */
export function ageOn(birth: string | null | undefined, day: string): number | null {
  if (!birth || !/^\d{4}-\d{2}-\d{2}/.test(birth) || !/^\d{4}-\d{2}-\d{2}/.test(day)) return null;
  const [by, bm, bd] = birth.slice(0, 10).split("-").map(Number) as [number, number, number];
  const [y, m, d] = day.slice(0, 10).split("-").map(Number) as [number, number, number];
  return y - by - (m < bm || (m === bm && d < bd) ? 1 : 0);
}

/** 18. Geburtstag (volljährig ab diesem Tag). */
export function majorityDate(birth: string): IsoDate {
  return addMonthsIso(birth.slice(0, 10), 18 * 12);
}

export interface MinorStatus {
  /** am Stichtag (Tag der Unterschrift) unter 18 */
  minor: boolean;
  age: number | null;
  /** unter 15: in Deutschland braucht die Mitwirkung bei Aufnahmen in der Regel eine behördliche Bewilligung */
  under15: boolean;
  /** Tag der Volljährigkeit, wenn er vor dem vereinbarten Ende liegt */
  turns18During: IsoDate | null;
}

/**
 * Stichtag der Minderjährigkeit (VERTRAEGE-R2-01, wie app.contract_minor_day): Maßgeblich ist die Unterschrift, und
 * unterschrieben wird frühestens heute. Liegt der Beginn später, zählt heute; liegt er zurück, der Beginn.
 */
export function minorDay(start: string, today?: string | null): string {
  return today && today.slice(0, 10) < start.slice(0, 10) ? today.slice(0, 10) : start;
}

/**
 * Minderjährig am Stichtag: am Tag der Unterschrift (mit `today` der frühere Tag aus Beginn und heute, sonst der Beginn).
 * Die Laufzeit über den 18. Geburtstag hinaus wird gemeldet, weil die Zustimmung der Eltern dann nur bis dahin trägt und der
 * Creator ab dann selbst entscheidet.
 */
export function minorStatus(birth: string | null | undefined, start: string, end?: string | null, today?: string | null): MinorStatus {
  const age = ageOn(birth, minorDay(start, today));
  if (age === null || !birth) return { minor: false, age: null, under15: false, turns18During: null };
  const minor = age < 18;
  const eighteen = majorityDate(birth);
  const turns = minor && !!end && eighteen <= end.slice(0, 10) ? eighteen : null;
  return { minor, age, under15: age < 15, turns18During: turns };
}
